/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import dynamic from "next/dynamic";
import React, { useState } from "react";
import { InView } from "react-intersection-observer";
import cx from "classnames";

import {
  IApplicationMenuScreenModel,
  IConfigurationModel,
  ScreenType,
  useCurrentUserSelector,
  useUserSubprofilesSelector,
} from "@nf/common";
import { useDunning } from "hooks";

import { AppHeaderVisibility } from "enums/AppHeaderVisibility";

const HeaderLogo = dynamic(() => import("../components/HeaderLogo"));
const HeaderSignIn = dynamic(
  () => import("../components/HeaderSignInDesktopTablet")
);
const HeaderUserInfo = dynamic(() => import("../components/HeaderUserInfo"));
const ApplicationMenu = dynamic(() => import("../../ApplicationMenu"));
const ApplicationHeader = dynamic(() => import("../../ApplicationHeader"));

const DunningBanner = dynamic(() => import("../../DunningBanner"), {
  ssr: false,
});

//import styles from "./AppHeaderDesktopTablet.module.scss";

export interface IAppHeaderProps {
  configuration?: IConfigurationModel;
  visibility?: AppHeaderVisibility;
  isLoggedIn: boolean;
  isChild?: boolean;
}

export const AppHeaderDesktopTablet = ({
  configuration,
  visibility = AppHeaderVisibility?.Default,
  isLoggedIn,
  isChild = false,
}: IAppHeaderProps) => {
  const [isInPrimaryView, setIsInPrimaryView] = useState(true);
  let applicationMenuScreenData: IApplicationMenuScreenModel | undefined =
    undefined;
  const user = useCurrentUserSelector();
  const { Data } = useUserSubprofilesSelector();
  const { isInDunning, wasDunningDialogDismissed } = useDunning();

  if (configuration && configuration.Screens) {
    const screen = configuration.Screens[ScreenType.ApplicationMenu];

    if (screen) {
      applicationMenuScreenData = screen as IApplicationMenuScreenModel;
    }
  }

  return (
    <InView onChange={(inView) => setIsInPrimaryView(inView)}>
      <header className="header">
        {isInDunning && wasDunningDialogDismissed && (
          <DunningBanner visible={isInPrimaryView} />
        )}

        <div
          className={cx("headerTabletDesktop", {
            ["headerTop"]: isInPrimaryView,
          })}
        >
          <div className={"headerLeft"}>
            <HeaderLogo profile={Data} configuration={configuration} />
            {!!(visibility & AppHeaderVisibility?.Menu) && (
              <ApplicationMenu
                screen={applicationMenuScreenData}
                isChild={isChild}
              />
            )}
          </div>
          <div className="headerRight">
            {!!(visibility & AppHeaderVisibility?.Header) && (
              <ApplicationHeader
                items={configuration?.Screens?.APPLICATION_HEADER?.Components}
              />
            )}
            {!!(visibility & AppHeaderVisibility.UserInfo) && (
              <HeaderUserInfo
                subprofilesState={Data}
                user={user}
                isLoggedIn={isLoggedIn}
              />
            )}
            {!!(visibility & AppHeaderVisibility?.SignIn) && <HeaderSignIn />}
          </div>
        </div>
      </header>
    </InView>
  );
};

export default AppHeaderDesktopTablet;
